import { Action, createHook, createStore } from "react-sweet-state";

import { authenticatedPostFetch } from "utility/authenticated_fetch";

// -----------------------------------------------------------------------------

export enum PagerdutyProgress {
  Unknown,
  Submitting,
  ErrorPagerdutyParam,
  Error,
  Success,
}

// -----------------------------------------------------------------------------

const initialState: PagerDutyStoreState = {
  progress: PagerdutyProgress.Unknown,
  listProgress: PagerdutyProgress.Unknown,
  services: [],
  connectionId: "",
};

// -----------------------------------------------------------------------------

export const usePagerdutyStore = createHook(
  createStore({
    initialState,
    actions: {
      submitParams,
    },
  })
);

// -----------------------------------------------------------------------------

function submitParams(config: string | null): Action<PagerDutyStoreState> {
  return async ({ setState }) => {
    setState({ progress: PagerdutyProgress.Submitting });

    if (!config) {
      return setState({
        progress: PagerdutyProgress.ErrorPagerdutyParam,
        errorDetail: "config is missing or empty",
      });
    }

    let configDict;
    try {
      configDict = JSON.parse(config);
    } catch (error) {
      return setState({
        progress: PagerdutyProgress.ErrorPagerdutyParam,
        errorDetail: "config is not valid JSON",
      });
    }

    const { account } = configDict;
    if (!account) {
      setState({
        progress: PagerdutyProgress.ErrorPagerdutyParam,
        errorDetail: "account key is not found in config",
      });
      return;
    }

    const { integration_keys: integrationKeys } = configDict;
    if (!integrationKeys) {
      setState({
        progress: PagerdutyProgress.ErrorPagerdutyParam,
        errorDetail: "integration_keys key is not found in config",
      });
      return;
    }

    const { name: pdOrgName, subdomain } = account;
    if (!pdOrgName) {
      return setState({
        progress: PagerdutyProgress.ErrorPagerdutyParam,
        errorDetail: "Name key is not found in account config",
      });
    }

    if (!subdomain) {
      return setState({
        progress: PagerdutyProgress.ErrorPagerdutyParam,
        errorDetail: "subdomain key is not found in account config",
      });
    }

    //
    try {
      const responsePayload = await authenticatedPostFetch(
        "api/organization/pagerduty",
        {
          integration_keys: integrationKeys,
          pd_org_name: pdOrgName,
          subdomain,
        }
      );

      return responsePayload.connection_uuid === undefined
        ? setState({
            progress: PagerdutyProgress.Error,
            errorDetail: JSON.stringify(responsePayload),
          })
        : setState({
            progress: PagerdutyProgress.Success,
            errorDetail: undefined,
            connectionId: responsePayload.connection_uuid,
          });
    } catch (error) {
      setState({
        progress: PagerdutyProgress.Error,
        errorDetail: (error as Error)?.message ?? "There was an error",
      });
    }
  };
}

// -----------------------------------------------------------------------------

interface PagerDutyStoreState {
  progress: PagerdutyProgress;
  errorDetail?: string;
  listProgress: PagerdutyProgress;
  services: string[];
  connectionId: string;
}
